/**
 * Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
 * analytics events keep the historical "owner note" name on purpose — do not rename them.
 *
 * Link-at-publish (owner-notes contract #81): a reader who is signed in to SoundGuys
 * publishes a note linked to their account, instantly, instead of landing in `held_review`
 * behind a "Sign in" CTA that would link in one click anyway.
 *
 * The Publish click is the consent moment — linking has side effects the reader must see
 * first (the note is tied to the account; the earlier held notes on this browser that the automatic
 * check accepted go live too, #64/#121 — appealed or out-of-resubmits notes stay with a moderator). So the identity row above the button says exactly what will happen,
 * the claim runs right BEFORE the note is sent, and nothing links on band open.
 *
 * Pure helpers here (unit-tested in the node jest environment); the band wires them.
 */
import { OwnerApiError } from './api'
import {
  GOOGLE_REQUIRED_CODE,
  GOOGLE_REQUIRED_MESSAGE,
  type OwnerClaimResponse,
  type OwnerMeResponse,
} from 'lib/dealhunt/types'
import type { OwnerCredential } from './auth-bridge'

/** Same shape DealHunt uses for `email_masked` (projections.maskEmail): `g•••@gmail.com`. */
export function maskEmail(email: string | null | undefined): string | null {
  if (!email) {
    return null
  }
  const at = email.indexOf('@')
  if (at <= 0 || at === email.length - 1) {
    return null
  }
  return `${email.slice(0, 1)}•••@${email.slice(at + 1)}`
}

export type OwnerProfileSummary = OwnerMeResponse['profile']

/** What the reader is told above the Publish button (design doc, "States" table). */
export interface IdentityRow {
  /** The sentence. */
  text: string
  /** A second line: the held notes that linking will also publish (state B). */
  extra: string | null
  /** Whether "Don't link" / "Link my account" is offered (never once the device is claimed). */
  canToggle: boolean
}

export interface IdentityRowInput {
  /** The signed-in reader's email, masked. `null` = signed out → no row, unless this browser's
   *  device is already linked (`profile.claimed`). */
  sessionEmail: string | null
  /** This browser's profile from `/me`, when a device exists. */
  profile: OwnerProfileSummary | null
  /** `held_review` notes on this browser's shelf that the link would publish — the ones
   *  `heldClaimOffered` accepts (#64/#121), not every held note. */
  heldCount: number
  /** The reader's choice for this band session. */
  linkAccount: boolean
  /** The sign-in gate is on for this create (`lib/owner-notes/signin-gate.ts`): publishing links,
   *  so `linkAccount` is forced and "Don't link" is never offered — the sentences (and the held
   *  count, the #81 consent line) are unchanged. */
  gated?: boolean
  /** A session exists (`OwnerAuth.signedIn`). Read only under the gate: a session that carries
   *  no email still links at Publish, so its row must still say so (the #81 consent surface) —
   *  it names "your SoundGuys account" without the masked address. */
  signedIn?: boolean
}

export function identityRow(input: IdentityRowInput): IdentityRow | null {
  const { profile, heldCount, gated = false } = input
  const linkAccount = gated || input.linkAccount
  const canToggle = !gated
  // Under the gate a signed-in session without an email still links: the row names the account
  // without the address rather than disappearing (ungated, no email = no session = no row).
  const sessionEmail = input.sessionEmail
  const sessionLinks = Boolean(sessionEmail) || (gated && Boolean(input.signedIn))
  const account = sessionEmail
    ? `your SoundGuys account (${sessionEmail})`
    : 'your SoundGuys account'
  if (!sessionLinks) {
    // A linked device with no session (signed out, or the session lapsed): the review still goes
    // out under the account's pseudonym — say so rather than nothing.
    return profile?.claimed
      ? { text: `Publishes as @${profile.handle} on this browser.`, extra: null, canToggle: false }
      : null
  }
  if (profile?.claimed) {
    // C/D: already linked — the masked email is the tell on a shared computer.
    const who = profile.email_masked ?? sessionEmail
    return {
      text: who
        ? `Publishes as @${profile.handle} (your account, ${who}).`
        : `Publishes as @${profile.handle} (your account).`,
      extra: null,
      canToggle: false,
    }
  }
  if (!linkAccount) {
    return {
      text: 'Publishes anonymously under your pseudonym.',
      extra: null,
      canToggle,
    }
  }
  if (profile) {
    // B: a device with a pseudonym, not yet linked.
    return {
      text: `Publishes as @${profile.handle}, linked to ${account} from now on.`,
      extra:
        heldCount > 0
          ? `Linking also publishes your ${heldCount} review${
              heldCount === 1 ? '' : 's'
            } waiting for a moderator.`
          : null,
      canToggle,
    }
  }
  // A: first note on this browser — the pseudonym does not exist until the mint.
  return {
    text: `Publishes under a pseudonym linked to ${account}. Your email is never shown.`,
    extra: null,
    canToggle,
  }
}

export const LINK_UNAVAILABLE_LEAD =
  "We couldn't link your account just now — your review published anonymously; try linking again."

/** The gated twin of `LINK_UNAVAILABLE_LEAD`: under the sign-in gate a failed link stops the
 *  send (fail-closed, `gatedLinkAction`), so nothing was published and the draft is kept. */
export const GATE_LINK_FAILED =
  "We couldn't link your account just now — nothing was sent. Try Publish again."

/** A 401 on the claim (`retry: 'sign_in'`): the ungated lead — the anonymous send went on. */
export const SIGN_IN_EXPIRED_LEAD = 'Your sign-in expired — sign in again to publish now.'
/** The gated twin of `SIGN_IN_EXPIRED_LEAD`: the send stopped, and the gate sells no speed
 *  (aca4da79), so no "now". */
export const GATE_SIGN_IN_EXPIRED = 'Your sign-in expired — sign in again to publish.'

/** Outcome of the claim that runs before the note is sent. */
export type LinkOutcome =
  | { kind: 'linked'; result: OwnerClaimResponse }
  /** 409: this browser's profile belongs to another account — the publish must NOT proceed. */
  | { kind: 'conflict'; message: string }
  /** Anything else: publish anonymously; `lead` is the CTA sentence, `retry` which button.
   *  Under the sign-in gate a fixable failure stops instead (`gatedLinkAction`). */
  | { kind: 'failed'; code: string; lead: string; retry: 'link' | 'sign_in' | 'none' }

export async function attemptLink(
  claim: (credential: OwnerCredential) => Promise<OwnerClaimResponse>,
  credential: OwnerCredential
): Promise<LinkOutcome> {
  try {
    const result = await claim(credential)
    return { kind: 'linked', result }
  } catch (e) {
    if (!(e instanceof OwnerApiError)) {
      return { kind: 'failed', code: 'error', lead: LINK_UNAVAILABLE_LEAD, retry: 'link' }
    }
    if (e.status === 409) {
      return {
        kind: 'conflict',
        message:
          e.code === 'already_claimed'
            ? "This browser's reviews belong to another SoundGuys account."
            : e.message,
      }
    }
    if (e.status === 401) {
      return {
        kind: 'failed',
        code: e.code,
        lead: SIGN_IN_EXPIRED_LEAD,
        retry: 'sign_in',
      }
    }
    // Terminal on this account: no verified email (DealHunt) or no email at all (SG's own
    // 400) — a "try again" button could never succeed, so the sentence stands alone.
    if (e.code === 'email_unverified' || e.code === 'email_required') {
      return { kind: 'failed', code: e.code, lead: e.message, retry: 'none' }
    }
    // Terminal until the reader adds Google or email (SG's joinable-identity gate, D5): the fixed sentence, which
    // `ownerLinkMessage` (components/owner-notes/OwnerLinkMessage) renders with its "account settings" link.
    if (e.code === GOOGLE_REQUIRED_CODE) {
      return { kind: 'failed', code: e.code, lead: GOOGLE_REQUIRED_MESSAGE, retry: 'none' }
    }
    return { kind: 'failed', code: e.code, lead: LINK_UNAVAILABLE_LEAD, retry: 'link' }
  }
}

/**
 * Under the sign-in gate, does the send go on after this claim? Fail-closed: a conflict (409)
 * stops as it always does, and a failure the reader can fix — try again (`link`) or sign in
 * again (`sign_in`) — stops with the draft kept (`GATE_LINK_FAILED` / the expired line). A
 * failure no retry can fix (`none`: unverified or missing email, Google required) continues
 * anonymously with the existing copy, so a written review is never trapped.
 */
export function gatedLinkAction(outcome: LinkOutcome): 'continue' | 'stop' {
  switch (outcome.kind) {
    case 'linked':
      return 'continue'
    case 'conflict':
      return 'stop'
    case 'failed':
      switch (outcome.retry) {
        case 'link':
        case 'sign_in':
          return 'stop'
        case 'none':
          return 'continue'
      }
  }
}

/** The accepted-note sentence for a linked publish (+ the OTHER held notes the link flipped). */
export function linkedAcceptCopy(
  productName: string,
  ownersNow: number,
  link: OwnerClaimResponse,
  /** This product's ids (page id, note `node_id`, aliases): its own held note, when the claim
   *  named it (#120), is this note — not an "earlier review". */
  thisProduct: ReadonlyArray<string> = []
): { lead: string; extra: string | null } {
  const lead = `You're owner #${ownersNow} of the ${productName}. Your owner review is live on the product page under @${link.handle}, linked to your SoundGuys account.`
  const n = link.published_node_ids
    ? link.published_node_ids.filter(id => !thisProduct.includes(id)).length
    : link.published_now
  return {
    lead,
    extra:
      n > 0
        ? `${n} earlier review${n === 1 ? '' : 's'} that ${
            n === 1 ? 'was' : 'were'
          } waiting for a moderator went live too — see My gear.`
        : null,
  }
}
